import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
	GetNewTermsAndConditionsBody,
	getNewTermsAndConditions,
	SaveUserTermsAndConditionsBody,
	useSaveUserTermsAndConditions,
} from '@uturn/api/v1';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	sonner,
} from '@uturn/ui-kit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TERMS_QUERY_KEY = ['terms-and-conditions', 'SHIPPER'] as const;

export const TermsAndConditionsModal = () => {
	const { t } = useTranslation();
	const { logout } = useAuth();
	const router = useRouter();
	const queryClient = useQueryClient();

	// Wrap the API function in useQuery instead of using `useGetNewTermsAndConditions`:
	// the generated hook is a useMutation (endpoint is POST), so it wouldn't auto-fire
	// on mount or give us caching, dedup, and focus-refetch.
	const { data: terms, isFetching } = useQuery({
		queryKey: TERMS_QUERY_KEY,

		queryFn: ({ signal }) =>
			getNewTermsAndConditions(
				GetNewTermsAndConditionsBody.SHIPPER,
				undefined,
				signal,
			),
		retry: false,
		select: (response) => response.data,
		staleTime: 5 * 60 * 1000,
	});

	const saveTerms = useSaveUserTermsAndConditions({
		mutation: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: TERMS_QUERY_KEY });
			},
			onError: () => {
				sonner.error(t('general.try-again-later', 'Please try again later.'));
			},
		},
	});

	const [isLoggingOut, setIsLoggingOut] = useState(false);

	const open = terms?.accepted === false;
	const isBusy = saveTerms.isPending || isFetching || isLoggingOut;

	const handleAccept = () => {
		saveTerms.mutate({ data: SaveUserTermsAndConditionsBody.SHIPPER });
	};

	const handleDecline = async () => {
		setIsLoggingOut(true);
		try {
			await logout();
			router.navigate({ to: '/sign-in' });
		} catch {
			sonner.error(t('general.try-again-later', 'Please try again later.'));
			setIsLoggingOut(false);
		}
	};

	if (!open) {
		return null;
	}

	return (
		<AlertDialog open>
			<AlertDialogContent className="grid max-h-[90vh] max-w-2xl grid-rows-[auto_1fr_auto] overflow-hidden md:max-w-3xl">
				<AlertDialogHeader>
					<AlertDialogTitle className="text-center sm:text-center">
						{t('terms-and-conditions.title', 'Terms and Conditions')}
					</AlertDialogTitle>
					<AlertDialogDescription className="text-center sm:text-center">
						{t(
							'terms-and-conditions.description',
							"We've updated our terms and conditions. Please review and accept them to continue.",
						)}
					</AlertDialogDescription>
				</AlertDialogHeader>

				<div className="space-y-4 overflow-y-auto px-2 text-center text-sm">
					<div
						className="text-foreground"
						dangerouslySetInnerHTML={{ __html: terms.changes ?? '' }}
					/>
				</div>

				<AlertDialogFooter className="sm:justify-center mt-6">
					<Button variant="outline" onClick={handleDecline} disabled={isBusy}>
						{t('terms-and-conditions.decline-and-logout', 'Decline and logout')}
					</Button>
					<Button onClick={handleAccept} disabled={isBusy}>
						{t(
							'terms-and-conditions.accept-and-continue',
							'Accept and continue',
						)}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
